import { useEffect, useMemo } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { usePermission } from '@/features/auth/permissions'
import { createEstate } from '@/features/hierarchy/estate'
import { useValueUnits } from '@/features/sensors/queries'
import {
  createSensorChart,
  deleteSensorChart,
  fetchLocationSensors,
  fetchSensorChart,
  fetchSensorChartReadingPage,
  fetchSensorCharts,
  resolveSpan,
  updateSensorChart,
} from './api'
import { buildSensorTree, indexTags } from './tree'

/**
 * Saved sensor charts. LIVE — real API, not gated on the demo flag.
 */

/**
 * Who may do what.
 *
 * ── Two permission families, and the screens need both ───────────────────
 * Every chart route takes `sensor_explorer.manage` OR `sensor_explorer.use`.
 * The sensor list the browser reads is a different route behind
 * `sensors.manage_sensors` OR `sensors.manage_connections`. Somebody holding
 * only `sensor_explorer.use` can open and delete charts but cannot browse a
 * single tag to put on one — a real gap, recorded in BACKEND-GAPS.md, and said
 * on screen rather than shown as an empty tree.
 *
 * Each hook on its own line and OR'd after: `a().granted || b().granted` would
 * skip a hook call on some renders.
 */
export function useSensorChartAccess() {
  const manage = usePermission('sensor_explorer.manage')
  const use = usePermission('sensor_explorer.use')
  const sensors = usePermission('sensors.manage_sensors')
  const connections = usePermission('sensors.manage_connections')
  return {
    isPending: manage.isPending || use.isPending,
    charts: manage.granted || use.granted,
    browse: sensors.granted || connections.granted,
  }
}

/** One page of the catalogue. Previous page held on screen while the next lands. */
export function useSensorCharts(params, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.sensorChartList(params),
    queryFn: ({ signal }) => fetchSensorCharts(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  })
}

/**
 * One chart.
 *
 * Seeded from any catalogue page already in the cache, so opening a card is
 * instant and the list walk in `fetchSensorChart` only runs on a cold link.
 * `initialDataUpdatedAt` carries that page's age, so a stale copy still
 * refetches rather than being trusted as new.
 */
export function useSensorChart(chartId) {
  const client = useQueryClient()
  return useQuery({
    queryKey: queryKeys.sensorChart(chartId),
    queryFn: ({ signal }) => fetchSensorChart(chartId, signal),
    enabled: Boolean(chartId),
    initialData: () => seededChart(client, chartId)?.chart,
    initialDataUpdatedAt: () => seededChart(client, chartId)?.updatedAt,
  })
}

function seededChart(client, chartId) {
  for (const [key, data] of client.getQueriesData({ queryKey: queryKeys.sensorChartList().slice(0, 2) })) {
    const chart = data?.items?.find((entry) => entry.id === chartId)
    if (chart) return { chart, updatedAt: client.getQueryState(key)?.dataUpdatedAt }
  }
  return null
}

/**
 * The sensor tree under one place.
 *
 * Three reads, two of them conditional, all cached:
 *   - every sensor in the subtree (one filtered walk of `GET /sensors`);
 *   - the place's child lines and machines from the hierarchy collections,
 *     through `createEstate` — the same cache the HierarchyPicker drills, so a
 *     generation the reader just opened in the picker is not fetched again;
 *   - the value-unit catalogue, for the symbol beside each tag.
 *
 * The hierarchy walk is one request for a line's machines, or one for a
 * plant's lines plus one per line for their machines, in parallel. A sensor's
 * row says which machine it is on but not which line the machine is on, so
 * there is no cheaper way to place it.
 */
export function useLocationSensorTree(place, { enabled = true } = {}) {
  const client = useQueryClient()
  const on = enabled && Boolean(place?.id && place?.tier)

  const sensors = useQuery({
    queryKey: queryKeys.locationSensors(place?.tier, place?.id),
    queryFn: ({ signal }) => fetchLocationSensors(place, signal),
    enabled: on,
  })

  const places = useQuery({
    queryKey: [...queryKeys.hierarchy, 'beneath', place?.tier, place?.id],
    queryFn: async () => {
      const estate = createEstate({ queryClient: client, account: null })
      if (place.tier === 'site') {
        const lines = await estate.children(place)
        const machines = (await Promise.all(lines.map((line) => estate.children(line)))).flat()
        return { lines, machines }
      }
      if (place.tier === 'line') return { lines: [], machines: await estate.children(place) }
      return { lines: [], machines: [] }
    },
    enabled: on,
  })

  const units = useValueUnits({ enabled: on })

  const tree = useMemo(
    () =>
      sensors.data && places.data
        ? buildSensorTree({ place, sensors: sensors.data, ...places.data })
        : null,
    // `place` by identity of what it names, not by object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sensors.data, places.data, place?.tier, place?.id, place?.name],
  )
  const index = useMemo(() => indexTags(tree), [tree])
  const unitSymbol = useMemo(() => {
    const map = new Map((units.data?.items ?? []).map((unit) => [unit.id, unit.symbol || unit.name]))
    return (id) => map.get(id) ?? ''
  }, [units.data])

  return {
    tree,
    index,
    unitSymbol,
    isPending: on && (sensors.isPending || places.isPending),
    isError: sensors.isError || places.isError,
    error: sensors.error ?? places.error,
    refetch: () => {
      sensors.refetch()
      places.refetch()
    },
  }
}

/**
 * Pages read on their own before the screen asks. Each is up to a page of rows
 * per tag; five covers a short window at most reporting rates, and the reader
 * can keep going from there. Unbounded would be hundreds of serial requests on
 * a 30-day window — the endpoint has no downsampling (BACKEND-GAPS.md).
 */
const AUTO_PAGES = 5

/**
 * A chart's readings over a span, a page at a time, oldest first.
 *
 * ── The span is fixed when the first page is read ─────────────────────────
 * A rolling "last 24h" resolves `now` once, in the first page, and every later
 * page continues to THAT end. Re-resolving per page would stitch pages from
 * different windows into one trace. Refresh restarts the sequence from a new
 * `now` (`reset`), rather than `refetch`, which would replay old cursors.
 *
 * Keyed on the tag set as well as the span, so adding or removing a tag reads
 * the new set rather than painting the old one.
 *
 * ── A new span keeps the old readings until it lands ──────────────────────
 * Earlier / Later change the span, and so the key. Without a placeholder the
 * chart would drop to a skeleton on every step; with the previous span's pages
 * held as placeholder data, the screen can slide the trace it already has and
 * let the new half fill in (`isPlaceholderData`). Held only across a span
 * change — never into another chart or another tag set, where the old trace
 * would be a different subject.
 */
export function useSensorChartReadings(chartId, spanInput, tagIds, { enabled = true } = {}) {
  const client = useQueryClient()
  const tagKey = [...tagIds].sort().join(',')
  const spanKey = spanInput.from && spanInput.to ? `${spanInput.from}/${spanInput.to}` : spanInput.window
  const queryKey = queryKeys.sensorChartReadings(chartId, spanKey, tagKey)

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const span = pageParam?.span ?? resolveSpan(spanInput)
      const page = await fetchSensorChartReadingPage(
        { chartId, from: pageParam?.from ?? span.from, to: span.to },
        signal,
      )
      return { ...page, span }
    },
    initialPageParam: null,
    getNextPageParam: (last) => (last?.next ? { from: last.next, span: last.span } : undefined),
    enabled: enabled && Boolean(chartId) && tagIds.length > 0,
    staleTime: Infinity,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === chartId && previousQuery?.queryKey[4] === tagKey ? previous : undefined,
  })

  const pages = useMemo(() => query.data?.pages ?? [], [query.data])
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError, isPlaceholderData } = query
  /* Not while the pages are the previous span's: their cursor belongs to it. */
  useEffect(() => {
    if (isPlaceholderData) return
    if (hasNextPage && !isFetchingNextPage && !isError && pages.length < AUTO_PAGES) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, isError, isPlaceholderData, pages.length, fetchNextPage])

  const readings = useMemo(() => pages.flatMap((page) => page.readings), [pages])

  return {
    readings,
    span: pages[0]?.span ?? null,
    /* Where the loaded readings stop, when there is more after it: the cursor
       the next page would start from. */
    loadedTo: hasNextPage ? pages.at(-1)?.next ?? null : null,
    isPending: query.isPending && query.fetchStatus !== 'idle',
    isPlaceholderData,
    isError,
    error: query.error,
    hasMore: hasNextPage,
    loadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
    reset: () => client.resetQueries({ queryKey }),
  }
}

/**
 * A create or an update sweeps the whole `sensorCharts` root — list, detail and
 * readings — after writing the server's answer into the detail cache.
 */
function useSensorChartMutation(mutationFn) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: (chart) => {
      client.setQueryData(queryKeys.sensorChart(chart.id), chart)
      return client.invalidateQueries({ queryKey: queryKeys.sensorCharts })
    },
  })
}

export const useCreateSensorChart = () => useSensorChartMutation(createSensorChart)

/**
 * The server's answer is written straight into the detail cache, so the signal
 * list moves the moment the PUT lands rather than after a second list walk.
 */
export const useUpdateSensorChart = () => useSensorChartMutation(updateSensorChart)

/**
 * Delete sweeps the LIST only. The detail screen is still mounted when this
 * lands, and invalidating its own query would refetch a chart that no longer
 * exists and flash a not-found before the navigation away. The screen drops the
 * detail and readings entries itself once it has left — see `SensorChartPage`.
 */
export function useDeleteSensorChart() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: deleteSensorChart,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.sensorChartList().slice(0, 2) }),
  })
}
